import { Checkbox, Divider, Group, Modal, ScrollArea, Stack, TextInput, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconChevronLeft, IconChevronRight, IconSearch } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import P2Regular from 'components/typography/P2Regular';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import panic from 'errors/panic';
import PermissionTemplateInput from 'components/inputs/permission-template-input/PermissionTemplateInput';
import { OrganizationListResponse } from 'api/actions/organization-list/organization-list-response';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import Button from 'components/Button';
import {
  combineOrganizationModuleFlags,
  OrganizationModuleFlags,
} from 'features/organization-modules/use-organization-modules';

type Step = 'organizations' | 'permission';

interface PickOrganizationsModalParams {
  title: string;
  permissionsTitle?: string;
  hiddenOrganizations: number[];
  opened: boolean;
  pickOrganizations?: boolean;
  pickPermissions?: boolean;
  autoSelectSingleOrganization?: boolean;
  targetOrganizationIds?: number[];
  initialStep?: Step;
  initialTemplateName: string;
  initialPermissions: number[];
  onClose: () => void;
  onPick: (value: {
    pickedOrganizations: {
      [organizationId: number]: {
        checked: boolean;
        templateName: string;
        organizationName: string;
        moduleFlags: OrganizationModuleFlags;
      };
    };
    permissions: number[];
    templateName: string;
  }) => void;
}

type FormValues = {
  pickedOrganizations: {
    [organizationId: number]: {
      checked: boolean;
      templateName: string;
      organizationName: string;
      moduleFlags: OrganizationModuleFlags;
    };
  };
  permissions: { templateName: string; permissions: number[] };
};

type Organization = OrganizationListResponse[number] & { search: string };

/**
 * Modal for selecting permissions of a user in an organization.
 */
export default function PickOrganizationsModal({
  title,
  permissionsTitle,
  hiddenOrganizations,
  opened,
  pickOrganizations = true,
  pickPermissions = true,
  autoSelectSingleOrganization = true,
  targetOrganizationIds = [],
  initialTemplateName,
  initialPermissions,
  onClose,
  onPick,
}: PickOrganizationsModalParams) {
  const { isTablet } = useResponsiveDesign();
  const [search, setSearch] = useState<string>('');
  const [step, setStep] = useState<'organizations' | 'permission'>(pickOrganizations ? 'organizations' : 'permission');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationsLoaded, setOrganizationsLoaded] = useState(false);

  const { getAction } = useApi();
  const theme = useMantineTheme();

  const filteredOrganizations = useMemo(() => {
    const normalizedSearch = search.toLowerCase(); // TODO: Remove diacritics

    return organizations.filter((organization) =>
      organization.organizationName.toLowerCase().includes(normalizedSearch)
    );
  }, [search, organizations]);

  const form = useForm<FormValues>({
    initialValues: {
      pickedOrganizations: {},
      permissions: { permissions: initialPermissions, templateName: initialTemplateName },
    },
  });

  const moduleFlags = combineOrganizationModuleFlags(
    organizations
      .filter((organization) =>
        pickOrganizations
          ? form.values.pickedOrganizations[organization.organizationId]?.checked
          : targetOrganizationIds.includes(organization.organizationId)
      )
      .map(({ priceOffersEnabled, hrEnabled, revisionsEnabled, formsEnabled, revisionTemplatesEnabled }) => ({
        priceOffersEnabled,
        hrEnabled,
        revisionsEnabled,
        formsEnabled,
        revisionTemplatesEnabled,
      }))
  );

  /**
   * Submits the form.
   */
  function submit({ pickedOrganizations, permissions }: FormValues) {
    onPick({ pickedOrganizations, permissions: permissions.permissions, templateName: permissions.templateName });
    onClose();
  }

  useEffect(() => {
    const organizationGetList = getAction('OrganizationList');
    // retrieve all people from the app
    // the backend will take care of filtering out the ones the current user doesn't have access to
    organizationGetList({ query: { filters: { 'showDiscarded.eq': 0, 'permissionSlug.eq': 'manage-users' } } })
      .then((organizations) => {
        const filtered = organizations
          .filter(({ organizationId }) => !hiddenOrganizations.includes(organizationId))
          .map((organization) => ({
            ...organization,
            search: organization.organizationName.toLowerCase(),
          }));

        if (filtered.length === 1 && autoSelectSingleOrganization) {
          const organization = filtered[0];
          form.setFieldValue(`pickedOrganizations.${organization.organizationId}`, {
            checked: true,
            templateName: 'no-template',
            organizationName: organization.organizationName,
            moduleFlags: {
              priceOffersEnabled: organization.priceOffersEnabled,
              hrEnabled: organization.hrEnabled,
              revisionsEnabled: organization.revisionsEnabled,
              formsEnabled: organization.formsEnabled,
              revisionTemplatesEnabled: organization.revisionTemplatesEnabled,
            },
          });

          setStep('permission');
        } else {
          const reduced = filtered.reduce((acc, curr) => {
            acc[curr.organizationId] = {
              checked: false,
              templateName: 'no-template',
              organizationName: curr.organizationName,
              moduleFlags: {
                priceOffersEnabled: curr.priceOffersEnabled,
                hrEnabled: curr.hrEnabled,
                revisionsEnabled: curr.revisionsEnabled,
                formsEnabled: curr.formsEnabled,
                revisionTemplatesEnabled: curr.revisionTemplatesEnabled,
              },
            };
            return acc;
          }, {} as FormValues['pickedOrganizations']);

          form.setFieldValue('pickedOrganizations', reduced);
        }

        setOrganizations(filtered);
        setOrganizationsLoaded(true);
      })
      .catch(panic);
  }, [getAction]);

  const multiStep = pickOrganizations && pickPermissions;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        step === 'organizations'
          ? title
          : multiStep && step === 'permission'
          ? 'Nastaviť oprávnenia v organizácii'
          : permissionsTitle
      }
      centered
    >
      <Divider color="gray.1" mb={24} />
      <form onSubmit={form.onSubmit(submit)}>
        {step === 'organizations' ? (
          <Stack spacing={24}>
            <TextInput
              rightSection={<IconSearch stroke="1.5" color={theme.colors.gray[6]} />}
              value={search}
              placeholder="Hľadať organizácie"
              size="md"
              onChange={(event) => setSearch(event.currentTarget.value)}
              name="objekto-search-organizations"
            />
            <ScrollArea h={410}>
              <Stack spacing={24} pb={24}>
                {filteredOrganizations.map((organization) => (
                  <Checkbox
                    key={organization.organizationId}
                    label={organization.organizationName}
                    size="lg"
                    form="pick-organizations-modal"
                    name={`objekto-pickedOrganizations.${organization.organizationId}.checked`}
                    {...form.getInputProps(`pickedOrganizations.${organization.organizationId}.checked`, {
                      type: 'checkbox',
                    })}
                  />
                ))}
                {isEmpty(organizations) && <P2Regular>Neboli nájdené žiadne organizácie.</P2Regular>}
              </Stack>
            </ScrollArea>
          </Stack>
        ) : (
          organizationsLoaded && (
            <PermissionTemplateInput
              {...form.getInputProps('permissions')}
              permissionType="organization"
              moduleFlags={moduleFlags}
            />
          )
        )}

        {/* TODO find a proper place to display this error */}
        <P2Regular color="red.9">{form.errors.pickedOrganizations}</P2Regular>
        <P2Regular color="red.9">{form.errors.permissions}</P2Regular>

        <Group spacing={isTablet ? 20 : 40} position="apart" mt={24}>
          <Button
            variant="subtle-gray"
            type="button"
            leftIcon={
              multiStep && step === 'permission' ? (
                <IconChevronLeft stroke="1.5" color={theme.colors.gray[7]} />
              ) : undefined
            }
            onClick={() => {
              if (multiStep && step === 'permission') {
                setStep('organizations');
              } else {
                onClose();
              }
            }}
          >
            {multiStep && step === 'permission' ? 'Späť' : 'Zrušiť'}
          </Button>
          {step === 'organizations' && (
            <Button
              variant="primary"
              size="md"
              type="button"
              rightIcon={<IconChevronRight stroke="1.5" color={theme.colors.gray[0]} />}
              onClick={() => setStep('permission')}
              disabled={Object.values(form.values.pickedOrganizations).every((value) => !value.checked)}
            >
              Pokračovať
            </Button>
          )}
          {step === 'permission' && (
            <Button
              variant="primary"
              size="md"
              type="submit"
              disabled={!organizationsLoaded || form.values.permissions.permissions.length === 0}
            >
              Uložiť {multiStep && ' a pridať organizácie'}
            </Button>
          )}
        </Group>
      </form>
    </Modal>
  );
}
